import React, { useState } from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Paper,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  MoreHoriz as MoreHorizIcon,
  TrendingUp as HoldingsIcon,
  PieChart as BudgetIcon,
  Assessment as ReportsIcon,
  Flag as GoalsIcon,
  SwapVert as ImportExportIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from '@tanstack/react-router';

/** Primary navigation items shown directly in the bottom bar. */
const PRIMARY_NAV = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { label: 'Transactions', icon: <ReceiptIcon />, path: '/transactions' },
  { label: 'Accounts', icon: <AccountBalanceIcon />, path: '/accounts' },
] as const;

/** Secondary navigation items shown in the "More" overflow menu. */
const MORE_ITEMS = [
  { label: 'Holdings', icon: <HoldingsIcon />, path: '/holdings' },
  { label: 'Budgets', icon: <BudgetIcon />, path: '/budgets' },
  { label: 'Goals', icon: <GoalsIcon />, path: '/goals' },
  { label: 'Reports', icon: <ReportsIcon />, path: '/reports' },
  { label: 'Import/Export', icon: <ImportExportIcon />, path: '/import-export' },
] as const;

/**
 * Bottom navigation bar for mobile devices.
 *
 * Displays four navigation items at the bottom of the screen: Dashboard,
 * Transactions, Accounts, and a "More" button that opens a menu with
 * links to additional pages. Only visible below the 'md' breakpoint.
 */
export const MobileBottomNav: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

  if (!isMobile) {
    return null;
  }

  /**
   * Determines the active navigation index based on the current route.
   *
   * Returns:
   *   number: Index of the active nav item, or 3 for "More" routes.
   */
  const getActiveIndex = (): number => {
    const idx = PRIMARY_NAV.findIndex(
      (item) =>
        location.pathname === item.path ||
        location.pathname.startsWith(`${item.path}/`)
    );
    if (idx !== -1) return idx;

    // Reason: If the current route matches a "More" menu item, highlight "More"
    const isMoreRoute = MORE_ITEMS.some(
      (item) =>
        location.pathname === item.path ||
        location.pathname.startsWith(`${item.path}/`)
    );
    return isMoreRoute ? 3 : 0;
  };

  const handleNavChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 3) {
      // "More" button - open the menu
      setMoreAnchor(_event.currentTarget as HTMLElement);
      return;
    }
    navigate({ to: PRIMARY_NAV[newValue].path });
  };

  const handleMoreItemClick = (path: string) => {
    setMoreAnchor(null);
    navigate({ to: path });
  };

  return (
    <>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
        elevation={3}
      >
        <BottomNavigation
          value={getActiveIndex()}
          onChange={handleNavChange}
          showLabels
        >
          {PRIMARY_NAV.map((item) => (
            <BottomNavigationAction
              key={item.label}
              label={item.label}
              icon={item.icon}
            />
          ))}
          <BottomNavigationAction
            label="More"
            icon={<MoreHorizIcon />}
          />
        </BottomNavigation>
      </Paper>
      <Menu
        anchorEl={moreAnchor}
        open={Boolean(moreAnchor)}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {MORE_ITEMS.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => handleMoreItemClick(item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
